import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined'
import NewReleasesOutlinedIcon from '@mui/icons-material/NewReleasesOutlined'
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined'
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt'
import PolicyOutlinedIcon from '@mui/icons-material/PolicyOutlined'
import SearchIcon from '@mui/icons-material/Search'
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined'
import AppBar from '@mui/material/AppBar'
import Box from '@mui/material/Box'
import { grey } from '@mui/material/colors'
import IconButton from '@mui/material/IconButton'
import { useTheme } from '@mui/material/styles'
import SvgIcon from '@mui/material/SvgIcon'
import Toolbar from '@mui/material/Toolbar'
import useMediaQuery from '@mui/material/useMediaQuery'
import { Cog, LightBulb } from '@styled-icons/heroicons-outline'
import React, { lazy, useCallback, useMemo, useState, Suspense } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate, Link } from 'react-router-dom-v5-compat'

import { ReactComponent as BeamerIcon } from 'assets/BeamerIcon.svg'
import { ReactComponent as NewPaymentIcon } from 'assets/NewPaymentIcon.svg'
import MenuToolTip from 'components/common/Tooltip'
import User from 'components/user/User'
import Constants from 'Constants'
import { canSeeSettingsTab, canSeeCustomersTab, canAddPayments } from 'data/permissions/permissionsSelectors'
import routes from 'router/routes'
import {
  setIsCreateCustomerDialogOpen,
  setIsSearchByEmailCustomerDialogOpen,
  setIsSearchByPhoneCustomerDialogOpen,
} from 'slices/customer/createCustomerSlice'
import T from 'T'
import { BACKGROUND } from 'theme/colors'
import { Z_INDEX } from 'theme/zIndex'

import NotificationBellMenu from '../notifications/notification-center/NotificationBellMenu'
import { useIsNotificationCenterAvailable } from '../notifications/notification-center/useIsNotificationCenterAvailable'

import BusinessLogo from './BusinessLogo'
import GlobalAddMenu from './GlobalAddMenu'
import MoreActionsMenu, { MoreActionsSection } from './MoreActionsMenu'
import NavigationMenu from './NavigationMenu'
import { BEAMER_SELECTOR_ID, SUGGEST_FEATURE_SELECTOR_ID } from './settings'

// eslint-disable-next-line import/no-cycle -- legacy lazy import that cycles through components/common; out of scope to refactor here
const TakePaymentsDialog = lazy(() => import('components/billing/groups/payment/take-payments'))

const { NAVIGATION } = Z_INDEX
const { PRIVACY_POLICY_PAGE } = Constants

const APP_BAR_SX = { height: '56px', backgroundColor: BACKGROUND.appbar, zIndex: NAVIGATION }
const TOP_BAR_ONLY_SX = { display: { xs: 'none', md: 'inline-flex' } }
const SETTINGS_ROUTE = `${routes.app.settings}/business-information`

interface AuthState {
  AuthReducer?: { userInfo?: { firstName?: string; lastName?: string; profilePicUri?: string } }
}

// Beamer and Intercom bind to these buttons by id, so on narrow screens they stay mounted but hidden and the menu clicks them.
const clickTopBarButton = (id: string) => () => document.getElementById(id)?.click()
const openPrivacyPolicy = () => window.open(PRIVACY_POLICY_PAGE, '_blank', 'noopener')

const Header = () => {
  // Same question the deactivation menus ask before promising a notification, so the two cannot disagree.
  const isNotificationCenterAvailable = useIsNotificationCenterAvailable()
  const theme = useTheme()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const isCompact = useMediaQuery(theme.breakpoints.down('md'))
  const [isOpenTakePaymentsDialog, setIsOpenTakePaymentsDialog] = useState(false)

  const userInfo = useSelector((state: AuthState) => state.AuthReducer?.userInfo, shallowEqual)
  const userCanAddPayments = useSelector(canAddPayments, shallowEqual)
  const userCanSeeCustomers = canSeeCustomersTab()
  const userCanSeeSettings = canSeeSettingsTab()

  const isSettingsActive = pathname.split('/').includes('settings')

  const handleToggleTakePaymentsDialog = useCallback(() => setIsOpenTakePaymentsDialog(isOpen => !isOpen), [])

  const moreSections = useMemo<MoreActionsSection[]>(() => {
    const customerActions = [
      { title: T.CREATE_A_NEW_CUSTOMER, icon: <PersonAddAltIcon />, onClick: () => dispatch(setIsCreateCustomerDialogOpen(true)) },
      { title: T.SEARCH_CUSTOMER_BY_EMAIL, icon: <SearchIcon />, onClick: () => dispatch(setIsSearchByEmailCustomerDialogOpen(true)) },
      { title: T.SEARCH_CUSTOMER_BY_PHONE, icon: <SearchIcon />, onClick: () => dispatch(setIsSearchByPhoneCustomerDialogOpen(true)) },
    ]
    const actions = [
      ...(userCanSeeCustomers ? customerActions : []),
      ...(userCanAddPayments ? [{ title: T.NEW_PAYMENT, icon: <PaymentsOutlinedIcon />, onClick: handleToggleTakePaymentsDialog }] : []),
      ...(userCanSeeSettings ? [{ title: T.SETTINGS, icon: <SettingsOutlinedIcon />, onClick: () => navigate(SETTINGS_ROUTE) }] : []),
    ]
    const helpAndInfo = [
      { title: T.WHATS_NEW_ON_HAULER_HERO, icon: <NewReleasesOutlinedIcon />, onClick: clickTopBarButton(BEAMER_SELECTOR_ID) },
      { title: T.SUGGEST_A_FEATURE, icon: <LightbulbOutlinedIcon />, onClick: clickTopBarButton(SUGGEST_FEATURE_SELECTOR_ID) },
      { title: T.PRIVACY_POLICY, icon: <PolicyOutlinedIcon />, onClick: openPrivacyPolicy },
    ]
    return [
      { title: T.ACTIONS, actions },
      { title: T.HELP_AND_INFO, actions: helpAndInfo },
    ]
  }, [userCanSeeCustomers, userCanAddPayments, userCanSeeSettings, dispatch, navigate, handleToggleTakePaymentsDialog])

  return (
    <Box display="block" sx={APP_BAR_SX}>
      <AppBar elevation={0} position="fixed" sx={APP_BAR_SX}>
        <Toolbar variant="dense">
          <BusinessLogo />
          <NavigationMenu />
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {userCanSeeCustomers && (
              <Box sx={TOP_BAR_ONLY_SX}>
                <GlobalAddMenu />
              </Box>
            )}
            {userCanAddPayments && (
              <MenuToolTip title={T.NEW_PAYMENT}>
                <IconButton sx={TOP_BAR_ONLY_SX} onClick={handleToggleTakePaymentsDialog}>
                  <NewPaymentIcon />
                </IconButton>
              </MenuToolTip>
            )}
            <MenuToolTip title={T.WHATS_NEW_ON_HAULER_HERO}>
              <IconButton id={BEAMER_SELECTOR_ID} size="large" sx={{ ...TOP_BAR_ONLY_SX, opacity: 0.9 }}>
                <BeamerIcon />
              </IconButton>
            </MenuToolTip>
            <MenuToolTip title={T.SUGGEST_A_FEATURE}>
              <IconButton id={SUGGEST_FEATURE_SELECTOR_ID} size="large" sx={TOP_BAR_ONLY_SX}>
                <SvgIcon sx={{ color: grey[500] }}>
                  <LightBulb />
                </SvgIcon>
              </IconButton>
            </MenuToolTip>
            {isNotificationCenterAvailable && <NotificationBellMenu />}
            {userCanSeeSettings && (
              <MenuToolTip title={T.SETTINGS}>
                <IconButton component={Link} size="large" sx={TOP_BAR_ONLY_SX} to={SETTINGS_ROUTE}>
                  <SvgIcon sx={{ color: isSettingsActive ? theme.palette.background.paper : grey[500] }}>
                    <Cog />
                  </SvgIcon>
                </IconButton>
              </MenuToolTip>
            )}
            {isCompact && <MoreActionsMenu sections={moreSections} />}
            <User
              firstName={userInfo?.firstName ?? ''}
              lastName={userInfo?.lastName ?? ''}
              profilePicture={userInfo?.profilePicUri ?? ''}
              showPrivacyPolicy={!isCompact}
            />
          </Box>
        </Toolbar>
      </AppBar>
      <Suspense>
        {isOpenTakePaymentsDialog && <TakePaymentsDialog isOpen={isOpenTakePaymentsDialog} onClose={handleToggleTakePaymentsDialog} />}
      </Suspense>
    </Box>
  )
}

export default Header
